import {
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
} from './https-axios.service';
import { CartItemUpdate, ManualCartItem } from './cart.service';
import { getRuntimeEnv } from "./runtime-config";

const API_BASE_URL = getRuntimeEnv("REACT_APP_NEST_API_URL");
if (!API_BASE_URL) {
  throw new Error('REACT_APP_NEST_API_URL is not defined');
}

const CART_BASE = `${API_BASE_URL.replace(/\/$/, "")}/cart`;

export type CartSubmitResponse = {
  ok: boolean;
  message: string;
  count: number;
  duplicateSkippedCount: number;
};

type ListCartResponse = {
  ok: boolean;
  items: ManualCartItem[];
};

type AddCartResponse = {
  status: 'added' | 'duplicate' | 'full';
  item?: ManualCartItem;
};

type UpdateCartResponse = {
  ok: boolean;
  item: ManualCartItem;
};

function withCountry(url: string, countryID?: string | null): string {
  const cid = String(countryID || '').trim();
  if (!cid) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}countryID=${encodeURIComponent(cid)}`;
}

export const cartApiService = {
  async listDraft(countryID?: string | null): Promise<ManualCartItem[]> {
    const { data } = await getRequest<ListCartResponse>(
      withCountry(CART_BASE, countryID),
    );
    return Array.isArray(data?.items) ? data.items : [];
  },

  async addScanned(
    payload: {
      barcode: string;
      price: string;
      availability: 'TRUE' | 'FALSE';
      notes?: string;
      avgWeight?: string;
    },
    countryID?: string | null,
  ): Promise<AddCartResponse> {
    const { data } = await postRequest<AddCartResponse>(
      withCountry(`${CART_BASE}/items`, countryID),
      {
        entryType: 'scanned',
        barcode: payload.barcode,
        price: payload.price,
        availability: payload.availability,
        notes: payload.notes,
        avgWeight: payload.avgWeight,
      },
    );
    return data;
  },

  async addManual(
    payload: {
      name: string;
      barcode?: string;
      category?: string;
      subCategory?: string;
      imageUrl?: string;
      quantityType?: string;
      avgWeight?: string;
      price: string;
      availability: 'TRUE' | 'FALSE';
      notes?: string;
    },
    countryID?: string | null,
  ): Promise<AddCartResponse> {
    const { data } = await postRequest<AddCartResponse>(
      withCountry(`${CART_BASE}/items`, countryID),
      {
        entryType: 'manual',
        name: payload.name,
        barcode: payload.barcode,
        category: payload.category,
        subCategory: payload.subCategory,
        imageUrl: payload.imageUrl,
        quantityType: payload.quantityType,
        avgWeight: payload.avgWeight,
        price: payload.price,
        availability: payload.availability,
        notes: payload.notes,
      },
    );
    return data;
  },

  async updateItem(
    itemId: string,
    updates: CartItemUpdate,
    countryID?: string | null,
  ): Promise<ManualCartItem> {
    const { data } = await patchRequest<UpdateCartResponse>(
      withCountry(`${CART_BASE}/items/${encodeURIComponent(itemId)}`, countryID),
      updates,
    );
    return data.item;
  },

  async removeItem(itemId: string): Promise<void> {
    await deleteRequest(`${CART_BASE}/items/${encodeURIComponent(itemId)}`);
  },

  async clearDrafts(): Promise<void> {
    await deleteRequest(CART_BASE);
  },

  async submit(payload: {
    businessName: string;
    phone: string;
    countryID?: string | null;
  }): Promise<CartSubmitResponse> {
    const { data } = await postRequest<CartSubmitResponse>(
      `${CART_BASE}/submit`,
      payload,
    );
    return data;
  },
};
